export interface ScadExample {
  name: string;
  code: string;
  category?: string;
}

export const SCAD_EXAMPLES: ScadExample[] = [
  {
    name: 'Feature Showcase',
    category: 'Basics',
    code: `// SCAD+ Feature Showcase
// Try editing the values — your model updates in real time!
$fn = 48;

// === Ground plate with mounting holes ===
color("#F5E6D3")
cube([100, 80, 3], center = true, fillet = -1, edges=[TOP]) {
  align(TOP, LEFT + FRONT,  inset = 6, mode = "cut")
    cylinder(h = 5, r = 2, center = true);
  align(TOP, RIGHT + FRONT, inset = 6, mode = "cut")
    cylinder(h = 5, r = 2, center = true);
  align(TOP, LEFT + BACK,   inset = 6, mode = "cut")
    cylinder(h = 5, r = 2, center = true);
  align(TOP, RIGHT + BACK,  inset = 6, mode = "cut")
    cylinder(h = 5, r = 2, center = true);

  align(TOP, CENTER + FRONT,  inset = 6, mode = "cut") {
    linear_extrude(0.2)
    text("SCAD+", size = 14, halign = "center", valign = "center");
  }
};

// === Meshing gears (left side) ===
color("#FFB5BA")
translate([-30, 0, 1.5])
  spur_gear(teeth = 16, mod = 1.5, width = 5, bore = 4);

color("#B5D4FF")
translate([-10.5, 0, 1.5])
  rotate([0, 0, 180 / 10])
    spur_gear(teeth = 10, mod = 1.5, width = 5, bore = 3);

// === Enclosure (right side) ===
color("#C1E1C1")
translate([15, -15, 1.5])
  enclosure(
    size = [35, 25, 18], wall = 1.5, r = 2,
    fillet_bottom = 1,
    mount = "screw", screw_size = "M2",
    part = "body"
  ){
    attach(FRONT, TOP, mode="cut") cube([10,10,10]);
    attach(LEFT, TOP, mode="cut") cube([10,10,10]);
      attach(BOTTOM,TOP) frame([20,10,1]) {
        attach(BOTTOM, TOP,align=RIGHT+FRONT, extend=[0,0,0]) standoff(h=3, r=3, d=2.0, hole_d = 1);
        attach(BOTTOM, TOP,align=LEFT+FRONT, extend=[0,0,0]) standoff(h=3, r=3, d=2.0, hole_d = 1);
        attach(BOTTOM, TOP,align=RIGHT+BACK, extend=[0,0,0]) standoff(h=3, r=3, d=2.0, hole_d = 1);
        attach(BOTTOM, TOP,align=LEFT+BACK, extend=[0,0,0]) standoff(h=3, r=3, d=2.0, hole_d = 1);
      }
  }

color("#D4B5FF")
translate([15, -15, 22])
  enclosure(
    size = [35, 25, 18], wall = 1.5, r = 2,
    chamfer_top = 0.8,
    mount = "screw", screw_size = "M2",
    part = "lid"
  ) {
    attach(TOP, TOP, mode="cut") cube([10,10,10]);
  }

// === Hardware (center-back) ===
color("#888")
translate([0, 25, 1.5])
  bolt("M5", length = 15, thread_length = 10);

color("#FFE066")
translate([12, 25, 1.5])
  nut("M5");

// === Spring (front-right) ===
color("#FF85A2")
translate([35, 20, 1.5])
  spring(r = 4, r_wire = 0.6, pitch = 2.5, turns = 5);`,
  },
  {
    name: 'Simple Cube',
    category: 'Basics',
    code: `// A simple centered cube
cube([20, 20, 20], center = true);`,
  },
  {
    name: 'Sphere with $fn',
    category: 'Basics',
    code: `// High-resolution sphere
$fn = 64;
sphere(r = 15);`,
  },
  {
    name: 'Cube minus Sphere',
    category: 'Basics',
    code: `// Classic CSG: cube with spherical cavity
$fn = 48;
difference() {
  cube([30, 30, 30], center = true);
  sphere(r = 19);
}`,
  },
  {
    name: 'Widget (3 Holes)',
    category: 'Basics',
    code: `// Cube with three cylindrical holes
$fn = 32;
difference() {
  cube([40, 40, 40], center = true);
  cylinder(h = 50, r = 8, center = true);
  rotate([90, 0, 0])
    cylinder(h = 50, r = 8, center = true);
  rotate([0, 90, 0])
    cylinder(h = 50, r = 8, center = true);
}`,
  },
  {
    name: 'Pipe Joint',
    category: 'Basics',
    code: `// T-shaped hollow pipe
$fn = 32;
difference() {
  union() {
    cylinder(h = 40, r = 10, center = true);
    rotate([90, 0, 0])
      cylinder(h = 40, r = 10, center = true);
  }
  union() {
    cylinder(h = 50, r = 7, center = true);
    rotate([90, 0, 0])
      cylinder(h = 50, r = 7, center = true);
  }
}`,
  },
  {
    name: 'Translated Shapes',
    category: 'Basics',
    code: `// Three shapes arranged with translate
$fn = 32;
union() {
  cube([15, 15, 15], center = true);
  translate([30, 0, 0])
    sphere(r = 10);
  translate([0, 30, 0])
    cylinder(h = 20, r = 7, center = true);
}`,
  },
  {
    name: 'Parametric Hollow Cylinder',
    category: 'Basics',
    code: `// Variables and math for a parametric ring
$fn = 48;
outer_r = 20;
inner_r = outer_r * 0.6;
height = 15;

difference() {
  cylinder(h = height, r = outer_r, center = true);
  cylinder(h = height + 10, r = inner_r, center = true);
}`,
  },
  {
    name: 'Cone',
    category: 'Basics',
    code: `// Cone using cylinder with r1/r2
$fn = 48;
cylinder(h = 30, r1 = 15, r2 = 0, center = true);`,
  },
  {
    name: 'Star Shape',
    category: 'Basics',
    code: `// Cube with spheres subtracted from faces
$fn = 24;
difference() {
  cube([30, 30, 30], center = true);
  translate([20, 0, 0]) sphere(r = 12);
  translate([-20, 0, 0]) sphere(r = 12);
  translate([0, 20, 0]) sphere(r = 12);
  translate([0, -20, 0]) sphere(r = 12);
  translate([0, 0, 20]) sphere(r = 12);
  translate([0, 0, -20]) sphere(r = 12);
}`,
  },
  {
    name: 'Scaled Intersection',
    category: 'Basics',
    code: `// Intersection of a sphere and a scaled cube
$fn = 48;
intersection() {
  sphere(r = 20);
  scale([1, 1, 0.5])
    cube([30, 30, 30], center = true);
}`,
  },
  {
    name: 'Hull',
    category: 'Boolean & Hull',
    code: `// Convex hull of two separated shapes
$fn = 32;
hull() {
  translate([-20, 0, 0])
    sphere(r = 8);
  translate([20, 0, 0])
    cylinder(h = 20, r = 8, center = true);
}`,
  },
  {
    name: 'Minkowski (Rounded Cube)',
    category: 'Boolean & Hull',
    code: `// Minkowski sum: cube + sphere = rounded cube
// (exact for convex operands)
$fn = 32;
minkowski() {
  cube([20, 20, 20], center = true);
  sphere(r = 3);
}`,
  },
  {
    name: 'Fillet & Chamfer',
    category: 'Boolean & Hull',
    code: `// Fillet and chamfer on cube edges
// Use anchors to select specific edges
$fn = 32;

// Fillet > 0: convex rounding (outward)
translate([-12, 0, 0])
  cube([8, 8, 8], fillet = 1.5);

// Chamfer: flat 45° cut
cube([8, 8, 8], chamfer = 1);

// Fillet < 0: concave fillet (inward scoop)
translate([12, 0, 0])
  cube([8, 8, 8], fillet = -1.5);

// Fillet only top face edges
translate([-12, 14, 0])
  cube([8, 8, 8], fillet = 1.5, edges = [TOP]);

// Fillet a single edge
translate([0, 14, 0])
  cube([8, 8, 8], fillet = 2, edges = [TOP+FRONT]);

// Chamfer 3 edges at a corner
translate([12, 14, 0])
  cube([8, 8, 8], chamfer = 1.5, edges = [TOP+FRONT+RIGHT]);`,
  },
  {
    name: 'Module: Pin',
    category: 'Modules',
    code: `// Reusable module with parameters
$fn = 32;

module pin(r = 4, h = 20) {
  union() {
    cylinder(h = h, r = r, center = true);
    translate([0, 0, h / 2])
      sphere(r = r * 1.8);
  }
}

pin();
translate([25, 0, 0]) pin(r = 6, h = 30);
translate([-25, 0, 0]) pin(r = 3, h = 15);`,
  },
  {
    name: 'Module: children()',
    category: 'Modules',
    code: `// Module using children() to wrap geometry
$fn = 32;

module rounded(r = 2) {
  minkowski() {
    children();
    sphere(r = r);
  }
}

module hollowed(wall = 0.3) {
  difference() {
    children();
    scale([1 - wall, 1 - wall, 1 - wall])
      children();
  }
}

hollowed(wall = 0.4)
  rounded(r = 1.5)
    cube([20, 20, 20], center = true);`,
  },
  {
    name: 'Linear Extrude',
    category: 'Extrusion',
    code: `// Extrude a 2D shape into 3D
$fn = 48;
linear_extrude(height = 30, twist = 90, slices = 30)
  difference() {
    circle(r = 15);
    translate([5, 0, 0])
      circle(r = 8);
  }`,
  },
  {
    name: 'Extruded Star',
    category: 'Extrusion',
    code: `// Extruded polygon star with taper
$fn = 32;
linear_extrude(height = 30, scale = 0.5, slices = 10) {
  polygon(points = [
    [20, 0], [7, 5],
    [0, 20], [-7, 5],
    [-20, 0], [-7, -5],
    [0, -20], [7, -5]
  ]);
}`,
  },
  {
    name: 'Linear Extrude + Chamfer / Fillet',
    category: 'Extrusion',
    code: `// Edge-treat a linear_extrude by wrapping it in chamfer() / fillet().
// Both modules act on the top and/or bottom rim of any 3D child.
// Best on convex prismatic profiles — for non-convex 2D shapes the
// fallback may extend the silhouette; in that case use tapered_extrude
// (its chamfer/fillet args have a built-in non-convex fix).

$fn = 64;

// (1) Chamfer both rims of a circular puck.
chamfer(c = 1.5)
  linear_extrude(10) circle(r = 12);

// (2) Chamfer top only — typical plate / spacer.
translate([40, 0, 0])
  chamfer(c = 1.2, top = true, bottom = false)
    linear_extrude(8) square([24, 18], center = true);

// (3) Fillet top + chamfer bottom on a star profile (mixed treatment).
translate([0, 40, 0])
  fillet(r = 1.5, top = true, bottom = false)
    chamfer(c = 1.0, top = false, bottom = true)
      linear_extrude(6) star(points = 6, r_outer = 14, r_inner = 7);

// (4) Both rims filleted on a hex profile.
translate([40, 40, 0])
  fillet(r = 1.5)
    linear_extrude(8) hexagon(r = 12);`,
  },
  {
    name: 'Tapered Extrude (wedge)',
    category: 'Extrusion',
    code: `// tapered_extrude: linear extrusion with a height that varies
// linearly along an axis. Useful for ramps, wedges, knife-edges.
//
// Args: tapered_extrude(h_start, h_end, length=auto, axis="x")
//   - h_start = height at the axis-min end of the profile
//   - h_end   = height at length mm further along the axis
//   - length  = taper length (default = profile bbox extent along axis)
//   - axis    = "x" (default) or "y"

$fn = 48;

// (1) A simple ramp — flat profile, tapers from 10 mm down to 2 mm.
tapered_extrude(10, 2)
  square([60, 20], center = true);

// (2) Y-axis taper, circle profile — ridge that's tall in the front,
//     flat in the back.
translate([0, 40, 0])
  tapered_extrude(15, 3, axis = "y")
    circle(r = 18);

// (3) Asymmetric taper with explicit length — useful when the profile
//     extends well past the taper region.
translate([100, 0, 0])
  tapered_extrude(12, 4, length = 30, axis = "x")
    union() {
      square([60, 20], center = true);
      translate([20, 0]) circle(r = 10);
    }`,
  },
  {
    name: 'For Loop Grid',
    category: 'Control Flow',
    code: `// Nested for loop: 4x4 grid of spheres
$fn = 16;
for (x = [0:3])
  for (y = [0:3]) {
    translate([x * 15, y * 15, 0])
      sphere(r = 5);
  }`,
  },
  {
    name: 'Array Iteration',
    category: 'Control Flow',
    code: `// Array iteration with indexing
$fn = 24;
sizes = [3, 5, 8, 10, 6];
for (i = [0:len(sizes) - 1]) {
  translate([i * 25, 0, 0])
    sphere(r = sizes[i]);
}`,
  },
  {
    name: 'Debug (#) Modifier',
    category: 'Modifiers & Color',
    code: `// Use # to highlight geometry for debugging
// The subtracted shapes appear in translucent pink
$fn = 32;
difference() {
  cube([40, 40, 40], center = true);
  #cylinder(h = 50, r = 10, center = true);
  #rotate([90, 0, 0])
    cylinder(h = 50, r = 10, center = true);
}`,
  },
  {
    name: '2D Shapes',
    category: '2D Shapes',
    code: `// 2D primitives rendered as thin layers
$fn = 48;

// circle with radius
translate([0, 0, 0]) circle(r = 15);

// circle with diameter
translate([40, 0, 0]) circle(d = 20);

// square (centered)
translate([70, 0, 0]) square([20, 15], center = true);

// polygon star
translate([0, 40, 0])
  polygon(points = [
    [15, 0], [5, 4],
    [0, 15], [-5, 4],
    [-15, 0], [-5, -4],
    [0, -15], [5, -4]
  ]);`,
  },
  {
    name: '2D Polygon with Holes',
    category: '2D Shapes',
    code: `// Polygon paths: outer boundary + inner hole
$fn = 32;
linear_extrude(height = 20)
  polygon(
    points = [
      [0, 0], [100, 0], [100, 100], [0, 100],
      [20, 20], [80, 20], [80, 80], [20, 80]
    ],
    paths = [[0,1,2,3], [7,6,5,4]]
  );`,
  },
  {
    name: 'Text',
    category: '2D Shapes',
    code: `// 2D text rendered as thin layer
$fn = 32;

// Basic text
text("Hello, World!", size = 12);

// Centered text
translate([0, -25, 0])
  text("SCAD+", size = 16, halign = "center");

// Right-aligned
translate([0, -45, 0])
  text("right", size = 10, halign = "right");`,
  },
  {
    name: 'Text: 3D Extruded',
    category: 'Extrusion',
    code: `// 3D text with linear_extrude
$fn = 32;

// Embossed text on a plate
color("#F5E6D3")
cube([80, 20, 3], center = true);

color("#FFB5BA")
translate([0, 0, 3])
linear_extrude(2)
  text("SCAD+", size = 14, halign = "center", valign = "center");`,
  },
  {
    name: 'Text: Engraved',
    category: 'Extrusion',
    code: `// Engraved text using difference
// Cube top is at z=2.5 (center=true). Text spans z=1..3 so it
// overlaps the top 1.5mm of the cube and engraves that depth.
$fn = 32;

difference() {
  cube([80, 25, 5], center = true);
  translate([0, 0, 1])
    linear_extrude(2)
      text("Engraved", size = 12, halign = "center", valign = "center");
}`,
  },
  {
    name: 'Rotate Extrude',
    category: 'Extrusion',
    code: `// Revolve a 2D profile around the Z axis
$fn = 48;
rotate_extrude()
  translate([20, 0])
    circle(r = 6);`,
  },
  {
    name: 'Rotate Extrude (Partial)',
    category: 'Extrusion',
    code: `// 270-degree partial revolution
$fn = 64;
rotate_extrude(angle = 270)
  translate([30, 0])
    difference() {
      square([20, 10], center = true);
      circle(r = 3);
    }`,
  },
  {
    name: 'Polyhedron',
    category: 'Extrusion',
    code: `// A pyramid defined by vertices and faces
polyhedron(
  points = [
    [0, 0, 0], [40, 0, 0],
    [40, 40, 0], [0, 40, 0],
    [20, 20, 30]
  ],
  faces = [
    [3, 2, 1, 0],
    [0, 1, 4],
    [1, 2, 4],
    [2, 3, 4],
    [3, 0, 4]
  ]
);`,
  },
  {
    name: 'Diameter Parameters',
    category: 'Basics',
    code: `// Using d= (diameter) instead of r= (radius)
$fn = 32;
sphere(d = 20);
translate([40, 0, 0])
  cylinder(h = 30, d = 20, center = true);
translate([80, 0, 0])
  cylinder(h = 30, d1 = 30, d2 = 10, center = true);`,
  },
  {
    name: 'Position: Face Anchors',
    category: 'Layout',
    code: `// position(anchor) places child's CENTER at parent's face
// 6 faces: TOP, BOTTOM, LEFT, RIGHT, FRONT, BACK
$fn = 32;
cube([80, 80, 30], center = true) {
  position(TOP)    sphere(r = 6);   // Z+ face
  position(BOTTOM) sphere(r = 6);   // Z- face
  position(RIGHT)  sphere(r = 6);   // X+ face
  position(LEFT)   sphere(r = 6);   // X- face
  position(FRONT)  sphere(r = 6);   // Y- face
  position(BACK)   sphere(r = 6);   // Y+ face
}`,
  },
  {
    name: 'Position vs Align vs Attach',
    category: 'Layout',
    code: `// Three layout modules for different placement needs
$fn = 32;
cube([80, 80, 80], center = true) {
  // position: child center at anchor point
  position(TOP) sphere(r = 10);

  // align: child flush on face (auto-anchors opposite face)
  align(RIGHT) cylinder(h = 30, r = 10);

  // attach: reorients child so it points outward from face
  attach(FRONT, BOTTOM) cylinder(h = 30, r = 10);
}`,
  },
  {
    name: 'Position: Edge Anchors',
    category: 'Layout',
    code: `// Combine 2 anchors with + for edges (12 edges on a cube)
$fn = 32;
cube([80, 80, 40], center = true) {
  // Horizontal top edges
  position(TOP + RIGHT) sphere(r = 4);
  position(TOP + LEFT)  sphere(r = 4);
  position(TOP + FRONT) sphere(r = 4);
  position(TOP + BACK)  sphere(r = 4);

  // Vertical edges
  position(RIGHT + FRONT) sphere(r = 4);
  position(LEFT + BACK)   sphere(r = 4);
}`,
  },
  {
    name: 'Align: Corner Placement',
    category: 'Layout',
    code: `// align(face, alignment) — flush on face, aligned to corner
// Face component negated for child (opposite face), alignment kept
$fn = 32;
cube([80, 80, 60], center = true) {
  // Flush on top, aligned to right+front corner
  align(TOP, RIGHT + FRONT) sphere(r = 8);
  align(TOP, LEFT + BACK) sphere(r = 8);

  // Flush on bottom, aligned to corners
  align(BOTTOM, RIGHT + FRONT) sphere(r = 8);
  align(BOTTOM, LEFT + BACK) sphere(r = 8);
}`,
  },
  {
    name: 'Position: Pad & Extend',
    category: 'Layout',
    code: `// pad: scalar (all axes) or vector (per-axis)
// extend: offset vector added after positioning
$fn = 32;
cube([60, 60, 60], center = true) {
  // Normal: sphere center at top face
  position(TOP) sphere(r = 5);

  // Scalar pad: expand all axes by 20
  position(TOP, pad = 20) sphere(r = 5);

  // Vector pad: expand only XY by 20, leave Z alone
  position(TOP + RIGHT, pad = [20, 20, 0]) sphere(r = 5);

  // extend: sphere at right face, shifted 30 further out
  position(RIGHT, extend = [30, 0, 0]) sphere(r = 5);
}`,
  },
  {
    name: 'Position: Axis Filtering',
    category: 'Layout',
    code: `// axis = which axes of the offset to apply
// X Y Z XY XZ YZ (default = all axes)
$fn = 32;
cube([100, 100, 30]) {
  // Only Z: sphere moves to top height, keeps its X,Y at 0
  position(TOP, axis = Z) sphere(r = 8);

  // Only XZ: moves X and Z, keeps original Y
  position(TOP + RIGHT, axis = XZ)
    sphere(r = 6);

  // Only X: moves to right face X, keeps Y,Z at 0
  position(RIGHT, axis = X) sphere(r = 6);
}`,
  },
  {
    name: 'Group with Layout',
    category: 'Layout',
    code: `// group() unions shapes; layout uses combined bounding box
$fn = 32;
group() {
  cube([80, 80, 20]);
  translate([100, 0, 0]) cube([80, 80, 20]);

  // Center of the combined bbox (between two cubes)
  position(CENTER) sphere(r = 8);

  // Cylinder flush on top of the group
  align(TOP) cylinder(h = 30, r = 5);

  // Sphere at combined right edge
  position(RIGHT) sphere(r = 6);
}`,
  },
  {
    name: 'Align: Table with Legs',
    category: 'Layout',
    code: `// Parametric table — try changing h to 0.5, 1, 3
// Legs reposition automatically via align with inset
$fn = 16;
w = 20; d = 12; h = 1;
leg_r = 0.5; leg_h = 8;
inset = 2;

cube([w, d, h], center = true) {
  // align(BOTTOM, corner, inset) — flush on bottom, inset from corner
  align(BOTTOM, LEFT + FRONT, inset = inset)
    cylinder(h = leg_h, r = leg_r);
  align(BOTTOM, RIGHT + FRONT, inset = inset)
    cylinder(h = leg_h, r = leg_r);
  align(BOTTOM, LEFT + BACK, inset = inset)
    cylinder(h = leg_h, r = leg_r);
  align(BOTTOM, RIGHT + BACK, inset = inset)
    cylinder(h = leg_h, r = leg_r);
}`,
  },
  {
    name: 'Align: Plate with Pegs',
    category: 'Layout',
    code: `// Pegs at corners using align with inset
// Safe for any plate thickness — try changing h
$fn = 32;
w = 30; d = 20; h = 1;
inset = 4;

cube([w, d, h], center = true) {
  // align(TOP, corner, inset) — flush on top, inset from corner
  align(TOP, LEFT + FRONT, inset = inset)
    cylinder(h = 2, r = 1);
  align(TOP, RIGHT + FRONT, inset = inset)
    cylinder(h = 2, r = 1);
  align(TOP, LEFT + BACK, inset = inset)
    cylinder(h = 2, r = 1);
  align(TOP, RIGHT + BACK, inset = inset)
    cylinder(h = 2, r = 1);
}`,
  },
  {
    name: 'Rounded Plate with Holes',
    category: 'Shapes',
    code: `// Rounded-corner plate with 4 holes
// hull for corners, mode="cut" for holes, try changing h
$fn = 32;
w = 30; d = 20; h = 2;
cr = 2;       // corner radius
inset = 4;    // hole distance from edge
hole_r = 1.5;

group() {
  // Rounded-corner plate: hull of 4 cylinders
  hull() {
    translate([ w/2-cr,  d/2-cr, 0])
      cylinder(h = h, r = cr, center = true);
    translate([-w/2+cr,  d/2-cr, 0])
      cylinder(h = h, r = cr, center = true);
    translate([ w/2-cr, -d/2+cr, 0])
      cylinder(h = h, r = cr, center = true);
    translate([-w/2+cr, -d/2+cr, 0])
      cylinder(h = h, r = cr, center = true);
  }

  // mode="cut" subtracts holes from the group
  position(LEFT + FRONT, mode = "cut",
    pad = [-inset, -inset, 0])
    cylinder(h = h + 1, r = hole_r, center = true);
  position(RIGHT + FRONT, mode = "cut",
    pad = [-inset, -inset, 0])
    cylinder(h = h + 1, r = hole_r, center = true);
  position(LEFT + BACK, mode = "cut",
    pad = [-inset, -inset, 0])
    cylinder(h = h + 1, r = hole_r, center = true);
  position(RIGHT + BACK, mode = "cut",
    pad = [-inset, -inset, 0])
    cylinder(h = h + 1, r = hole_r, center = true);
}`,
  },
  {
    name: 'Align: Center at Origin',
    category: 'Layout',
    code: `// Standalone align(anchor) moves child so anchor point goes to origin
// Useful for CSG: both shapes share the same reference point
$fn = 32;

// Centered hole through a plate
difference() {
  align(CENTER) cube([100, 100, 20]);
  align(CENTER) cylinder(h = 50, r = 10);
}`,
  },
  {
    name: 'Align: from Parameter',
    category: 'Layout',
    code: `// Standalone mode: from overrides which point goes to origin
$fn = 32;
union() {
  // Cube bottom at origin — cube sits above z=0
  align(from = BOTTOM) cube([40, 40, 40]);

  // Cube top at origin — cube hangs below z=0
  translate([60, 0, 0])
    align(from = TOP) cube([40, 40, 40]);

  // Cylinder left face at origin
  translate([120, 0, 0])
    align(from = LEFT) cylinder(h = 40, r = 10);
}`,
  },
  {
    name: 'Align: Axis Filtering',
    category: 'Layout',
    code: `// Standalone mode: axis controls which axes are aligned
// Unaffected axes keep their original position
$fn = 32;
union() {
  // Center XY only — cube bottom stays at z=0
  align(CENTER, axis = XY) cube([80, 80, 20]);

  // Center X only — cylinder stays at Y=0, Z=0
  align(CENTER, axis = X)
    cylinder(h = 40, r = 10);

  // Center Z only (move top to origin)
  translate([60, 0, 0])
    align(TOP, axis = Z) cube([30, 30, 30]);
}`,
  },
  {
    name: 'Attach: Chain',
    category: 'Layout',
    code: `// Four cubes chained along X using nested attach
// attach() reorients child — LEFT face meets RIGHT face
cube([50, 50, 50]) {
  attach(RIGHT, LEFT, overlap = -10)
    cube([50, 50, 50]) {
      attach(RIGHT, LEFT, overlap = -10)
        cube([50, 50, 50]) {
          attach(RIGHT, LEFT, overlap = -10)
            cube([50, 50, 50]);
        };
    };
};`,
  },
  {
    name: 'Panel with Window',
    category: 'Projects',
    code: `// Panel with window — cut creates opening, children are added inside
$fn = 32;
cube([30, 20, 1], center = true) {
  // Display window: cut opening, screen placed inside
  position(CENTER, mode = "cut")
    cube([12, 6, 2], center = true) {
      position(CENTER)
        cube([10, 4, 0.5], center = true);
    };

  // Circular port: cut hole, lens placed inside
  position(CENTER + RIGHT, mode = "cut", pad = [-4, -4, 0])
    cylinder(h = 2, r = 2) {
      position(CENTER)
        sphere(r = 1.5);
    };

  // Knob on surface (normal add)
  align(FRONT, LEFT, pad = [-4, -4, 0])
    cylinder(h = 2, r = 1.5);
}`,
  },
  {
    name: 'Radio Front Panel',
    category: 'Projects',
    code: `// Front panel — rounded plate with LCD, speakers, encoder
// Demonstrates cut-mode with children add-back
$fn = 100;
width = 220;
height = 60;
depth = 4;

lcd_width = 79;
lcd_height = 21.5;
lcd_mount_width = 90;
lcd_mount_height = 31.2;
box_corner_radius = 5;

lcd_y = -10;
spk_x = width/2 - 25 - 5;

module rcube(size, r) {
  hull() {
    translate([ size[0]/2 - r,  size[1]/2 - r, 0])
      cylinder(h = size[2], r = r, center = true);
    translate([-(size[0]/2 - r),  size[1]/2 - r, 0])
      cylinder(h = size[2], r = r, center = true);
    translate([ size[0]/2 - r, -(size[1]/2 - r), 0])
      cylinder(h = size[2], r = r, center = true);
    translate([-(size[0]/2 - r), -(size[1]/2 - r), 0])
      cylinder(h = size[2], r = r, center = true);
  }
}

group() {
  // Panel body: centered rounded rectangle
  rcube([width, height, depth], box_corner_radius);

  // LCD view window — cut opening, screen glass added back inside
  position(CENTER, mode = "cut", extend = [0, lcd_y, 0])
    cube([lcd_width, lcd_height, depth + 1], center = true) {
      position(CENTER)
        cube([lcd_width - 2, lcd_height - 2, 0.5], center = true);
    };

  // LCD mount recess (3mm deep from back)
  position(CENTER, mode = "cut", extend = [-0.5, lcd_y + 2.65, 0.5])
    cube([lcd_mount_width, lcd_mount_height, 3], center = true);

  // LCD solder pins recess
  position(CENTER, mode = "cut", extend = [47, lcd_y + 2.95, 0.5])
    cube([7, 23, 3], center = true);

  // Encoder hole
  position(CENTER, mode = "cut", extend = [0, height/4, 0])
    cylinder(r = 7/2, h = depth + 2, center = true);

  // Left speaker hole
  position(CENTER, mode = "cut", extend = [spk_x, 0, 0])
    cylinder(r = 50/2, h = depth + 2, center = true);

  // Left speaker mount recess
  position(CENTER, mode = "cut", extend = [spk_x, 0, 1])
    rcube([53, 53, 3], 3);

  // Right speaker hole
  position(CENTER, mode = "cut", extend = [-spk_x, 0, 0])
    cylinder(r = 50/2, h = depth + 2, center = true);

  // Right speaker mount recess
  position(CENTER, mode = "cut", extend = [-spk_x, 0, 1])
    rcube([53, 53, 3], 3);
}`,
  },
  {
    name: 'Frame: Panel Layout',
    category: 'Layout',
    code: `// Panel with a frame layout region
// 4 pegs at corners + center knob, all inside the frame
$fn = 32;

cube([80, 50, 2], center = true) {
  align(TOP)
    frame([60, 40, 8]) {
      // 4 corner pegs — align flush on top at corners
      align(TOP, LEFT + FRONT)
        cylinder(h = 6, r = 2);
      align(TOP, RIGHT + FRONT)
        cylinder(h = 6, r = 2);
      align(TOP, LEFT + BACK)
        cylinder(h = 6, r = 2);
      align(TOP, RIGHT + BACK)
        cylinder(h = 6, r = 2);

      // Center knob
      align(TOP)
        cylinder(h = 4, r1 = 5, r2 = 3);
    };
}`,
  },
  {
    name: 'If / Else',
    category: 'Control Flow',
    code: `// Conditionals and ternary expressions
$fn = 24;
for (i = [0:5]) {
  translate([i * 25, 0, 0])
    if (i % 2 == 0) {
      cube(10, center = true);
    } else {
      sphere(r = 6);
    }
}

// Ternary in expression
n = 4;
r = n > 3 ? 15 : 5;
translate([0, 40, 0]) sphere(r = r);`,
  },
  {
    name: 'Colors & Transparency',
    category: 'Modifiers & Color',
    code: `// Color with named colors, hex codes, and transparency
// Each top-level shape can have its own color
$fn = 32;

// Named colors
color("red")
  translate([-60, 0, 0])
    sphere(r = 12);

color("green")
  translate([-30, 0, 0])
    sphere(r = 12);

color("blue")
  translate([0, 0, 0])
    sphere(r = 12);

// Hex colors
color("#FFB5BA")  // Pastel rose
  translate([30, 0, 0])
    sphere(r = 12);

color("#B5E5FF")  // Pastel sky
  translate([60, 0, 0])
    sphere(r = 12);

// Transparency with alpha
color("cyan", alpha = 0.4)
  translate([0, 0, 30])
    cube([160, 40, 10], center = true);

// Multiple transparent layers
color("#D4B5FF", alpha = 0.5)  // Pastel lavender
  translate([0, -40, 0])
    cube([80, 20, 30], center = true);

color("#FFD1DC", alpha = 0.6)  // Pastel blush
  translate([0, -40, 10])
    cube([60, 25, 20], center = true);`,
  },
  {
    name: 'Colored Assembly',
    category: 'Modifiers & Color',
    code: `// Multi-colored assembly with transparent window
$fn = 32;

// Base plate - sand color
color("#F5E6D3")
  cube([200, 150, 10], center = true);

// Main body - sage green
color("#C1E1C1")
  translate([0, 0, 30])
    cube([160, 120, 50], center = true);

// Window - transparent sky blue
color("#B5E5FF", alpha = 0.3)
  translate([0, -61, 35])
    cube([100, 5, 30], center = true);

// Buttons - coral
color("#FFB5B5")
  translate([-50, 61, 30])
    rotate([90, 0, 0])
      cylinder(h = 5, r = 15);

color("#FFB5B5")
  translate([0, 61, 30])
    rotate([90, 0, 0])
      cylinder(h = 5, r = 15);

color("#FFB5B5")
  translate([50, 61, 30])
    rotate([90, 0, 0])
      cylinder(h = 5, r = 15);

// Antenna - lavender
color("#D4B5FF")
  translate([70, 0, 60])
    cylinder(h = 80, r1 = 8, r2 = 3);`,
  },
  {
    name: 'Chamfered Cylinder',
    category: 'Shapes',
    code: `// Chamfered cylinder — all parameter variations
$fn = 48;

// 1. Uniform chamfer on both edges (r + chamfer)
translate([-60, 0, 0])
  chamfered_cylinder(r = 10, h = 15, chamfer = 2);

// 2. Different top/bottom chamfers
translate([-35, 0, 0])
  chamfered_cylinder(r = 10, h = 15, chamfer_top = 3, chamfer_bot = 1);

// 3. By diameter instead of radius
translate([-10, 0, 0])
  chamfered_cylinder(d = 20, h = 15, chamfer = 2);

// 4. Conical with r1/r2
translate([15, 0, 0])
  chamfered_cylinder(r1 = 12, r2 = 8, h = 15, chamfer = 1.5);

// 5. Conical with d1/d2
translate([40, 0, 0])
  chamfered_cylinder(d1 = 24, d2 = 16, h = 15, chamfer_top = 2, chamfer_bot = 1);

// 6. Centered on Z axis
color("#B5D4FF")
  translate([65, 0, 0])
    chamfered_cylinder(r = 10, h = 15, chamfer = 2, center = true);

// 7. Custom $fn (low-poly hexagonal)
translate([0, 30, 0])
  chamfered_cylinder(r = 10, h = 15, chamfer = 2, $fn = 6);

// 8. Only top chamfer
translate([25, 30, 0])
  chamfered_cylinder(r = 10, h = 15, chamfer_top = 3);

// 9. Only bottom chamfer
translate([50, 30, 0])
  chamfered_cylinder(r = 10, h = 15, chamfer_bot = 3);

// 10. No chamfer (same as cylinder)
translate([-25, 30, 0])
  chamfered_cylinder(r = 10, h = 15);`,
  },
  {
    name: 'Filleted Cylinder',
    category: 'Shapes',
    code: `// Cylinder with fillet and chamfer edges
$fn = 48;

// 1. Uniform fillet on both edges
translate([-50, 0, 0])
  cylinder(r = 10, h = 20, fillet = 2);

// 2. Chamfer on both edges
translate([-25, 0, 0])
  cylinder(r = 10, h = 20, chamfer = 2);

// 3. Fillet top, chamfer bottom
cylinder(r = 10, h = 20, fillet_top = 3, chamfer_bot = 1.5);

// 4. Only top fillet
translate([25, 0, 0])
  cylinder(r = 10, h = 20, fillet_top = 3);

// 5. Conical with fillets
translate([50, 0, 0])
  cylinder(r1 = 15, r2 = 8, h = 20, fillet = 2);

// 6. Centered with fillet
color("#B5D4FF")
  translate([-25, 30, 0])
    cylinder(r = 10, h = 20, fillet = 2, center = true);

// 7. Different top/bottom chamfers
translate([0, 30, 0])
  cylinder(r = 10, h = 20, chamfer_top = 3, chamfer_bot = 1);

// 8. Pointed cone (top fillet ignored)
translate([25, 30, 0])
  cylinder(r1 = 12, r2 = 0, h = 20, fillet_bot = 2);`,
  },
  {
    name: 'Filleted Pipe',
    category: 'Shapes',
    code: `// Pipe with fillet and chamfer on top/bottom edges
$fn = 48;

// 1. Uniform fillet on all corners
translate([-40, 0, 0])
  pipe(r = 10, h = 20, t = 2, fillet = 0.5);

// 2. Chamfer on all corners
translate([-15, 0, 0])
  pipe(r = 10, h = 20, t = 2, chamfer = 0.5);

// 3. Fillet top, chamfer bottom
translate([10, 0, 0])
  pipe(r = 10, h = 20, t = 2, fillet_top = 0.5, chamfer_bot = 0.5);

// 4. Only bottom fillet
translate([35, 0, 0])
  pipe(r = 10, h = 20, t = 2, fillet_bot = 0.5);

// 5. Thick wall with fillet
color("#C1E1C1")
  translate([-15, 30, 0])
    pipe(r = 12, h = 20, t = 4, fillet = 1);

// 6. Centered with chamfer
translate([10, 30, 0])
  pipe(r = 10, h = 20, t = 2, chamfer = 0.5, center = true);`,
  },
  {
    name: 'Gen6 Cover',
    category: 'Projects',
    code: `// Gen6 Generator Cover — SCAD recreation with spokes
$fn = 64;

difference() {
  union() {
    // Bottom plate + tapered skirt (z=0 to z=1.5)
    chamfered_cylinder(r1 = 26.91, r2 = 25.2, h = 1.5);

    // Outward flare from skirt to wall (z=1.5 to z=3.086)
    translate([0, 0, 1.5])
      cylinder(r1 = 25.2, r2 = 30, h = 1.586);

    // Slightly tapered wall (z=3.086 to z=11)
    translate([0, 0, 3.086])
      cylinder(r1 = 30, r2 = 29.1, h = 7.914);

    // Upper wall + rim flare (z=11 to z=15, open top)
    translate([0, 0, 11])
      cylinder(r = 29.1, h = 3.2);
    translate([0, 0, 14.2])
      chamfered_cylinder(r1 = 29.1, r2 = 30, h = 0.8, chamfer_top = 0.1);

    // Hub above bottom plate (r=7, z=1.5 to z=2.5)
    translate([0, 0, 1.5])
      cylinder(r = 7, h = 1);

    // Narrower boss above hub (r=6, z=2.5 to z=3.5)
    translate([0, 0, 2.5])
      cylinder(r = 6, h = 1);
  }

  // Interior cavity — hollow from plate top through open top
  // Preserves hub, boss, and spoke ribs
  translate([0, 0, 1.5])
    difference() {
      cylinder(r = 28.2, h = 14);
      cylinder(r = 7, h = 1);
      translate([0, 0, 1])
        cylinder(r = 6, h = 1);
      // 8 spoke ribs — structural fins from hub to wall
      for (i = [0 : 7]) {
        rotate([0, 0, i * 45 + 22.5])
          linear_extrude(h = 7)
            polygon(points = [
              [6 * cos(0), 6 * sin(0)],
              [28.2 * cos(0), 28.2 * sin(0)],
              [28.2 * cos(22.5), 28.2 * sin(22.5)],
              [6 * cos(22.5), 6 * sin(22.5)]
            ]);
      }
    }

  // 8 spoke windows — pie-shaped cutouts through bottom plate
  for (i = [0 : 7]) {
    rotate([0, 0, i * 45])
      translate([0, 0, -0.1])
        linear_extrude(h = 1.7)
          polygon(points = [
            [7 * cos(0), 7 * sin(0)],
            [25 * cos(0), 25 * sin(0)],
            [25 * cos(22.5), 25 * sin(22.5)],
            [7 * cos(22.5), 7 * sin(22.5)]
          ]);
  }

  // Central bore (r=3 through everything)
  cylinder(r = 3, h = 16, center = true);
}`,
  },
  {
    name: 'Spur Gear',
    category: 'Gears',
    code: `// Involute spur gear with center bore
$fn = 64;
spur_gear(teeth = 20, mod = 2, width = 5, bore = 6);`,
  },
  {
    name: 'Gear Train',
    category: 'Gears',
    code: `// Two meshing spur gears at correct pitch distance
$fn = 64;
mod = 2;
t1 = 12;
t2 = 20;
w = 5;

// Driving gear (coral)
color("#FFB5B5")
  spur_gear(teeth = t1, mod = mod, width = w, bore = 4);

// Driven gear — offset by mod*(t1+t2)/2, rotated half-tooth
color("#B5D4FF")
  translate([mod * (t1 + t2) / 2, 0, 0])
    rotate([0, 0, 180 / t2])
      spur_gear(teeth = t2, mod = mod, width = w, bore = 6);`,
  },
  {
    name: 'Helical Gear',
    category: 'Gears',
    code: `// Helical gear — teeth spiral along the face width
$fn = 64;
helical_gear(teeth = 20, mod = 2, width = 8, helix_angle = 20, bore = 6);`,
  },
  {
    name: 'Herringbone Gear',
    category: 'Gears',
    code: `// Double-helical (herringbone) gear — V-shaped teeth
$fn = 64;
herringbone_gear(teeth = 24, mod = 2, width = 10, helix_angle = 30, bore = 8);`,
  },
  {
    name: 'Rack and Pinion',
    category: 'Gears',
    code: `// Rack with meshing pinion gear
$fn = 64;
mod = 2;
teeth = 12;
w = 5;

// Pinion gear at pitch radius above rack
color("#C1E1C1")
  translate([0, mod * teeth / 2, 0])
    spur_gear(teeth = teeth, mod = mod, width = w, bore = 4);

// Rack
color("#FFE4B5")
  rack(teeth = 8, mod = mod, width = w);`,
  },
  {
    name: 'Bevel Gear',
    category: 'Gears',
    code: `// Conical bevel gear for perpendicular shaft drives
$fn = 64;
bevel_gear(teeth = 20, mod = 2, width = 5, cone_angle = 45, bore = 6);`,
  },
  {
    name: 'Worm Gear',
    category: 'Gears',
    code: `// Worm (helical screw) — double-start thread
$fn = 64;
worm(starts = 2, mod = 2, length = 20, r = 5, bore = 3);`,
  },
  {
    name: 'L-Profile',
    category: 'Profiles',
    code: `// L-shaped angle beam — all parameter variations
$fn = 32;

// 1. Plain L-profile (sharp corners)
l_profile(w = 5, h = 8, t = 1, l = 20);

// 2. Inner chamfer only
translate([8, 0, 0])
  l_profile(w = 5, h = 8, t = 1, l = 20, chamfer = 0.8);

// 3. Inner fillet only
translate([16, 0, 0])
  l_profile(w = 5, h = 8, t = 1, l = 20, fillet = 0.8);

// 4. Outside chamfer only
translate([24, 0, 0])
  l_profile(w = 5, h = 8, t = 1, l = 20, chamfer_outside = 0.8);

// 5. Outside fillet only
translate([32, 0, 0])
  l_profile(w = 5, h = 8, t = 1, l = 20, fillet_outside = 0.8);

// 6. Inner chamfer + outside chamfer
translate([0, 12, 0])
  l_profile(w = 5, h = 8, t = 1, l = 20, chamfer = 0.8, chamfer_outside = 0.5);

// 7. Inner fillet + outside fillet
translate([8, 12, 0])
  l_profile(w = 5, h = 8, t = 1, l = 20, fillet = 0.8, fillet_outside = 0.5);

// 8. Heavy wall thickness
translate([16, 12, 0])
  l_profile(w = 6, h = 8, t = 2.5, l = 20, fillet = 1, fillet_outside = 0.6);

// 9. Centered on all axes
color("#B5D4FF")
  translate([26, 12, 0])
    l_profile(w = 5, h = 8, t = 1, l = 20, fillet = 0.5, fillet_outside = 0.3, center = true);`,
  },
  {
    name: 'U-Profile',
    category: 'Profiles',
    code: `// U-shaped channel beam — all parameter variations
$fn = 32;

// 1. Plain U-profile (sharp corners)
u_profile(w = 8, h = 6, t = 1, l = 20);

// 2. Inner chamfer only
translate([12, 0, 0])
  u_profile(w = 8, h = 6, t = 1, l = 20, chamfer = 0.8);

// 3. Inner fillet only
translate([24, 0, 0])
  u_profile(w = 8, h = 6, t = 1, l = 20, fillet = 0.8);

// 4. Outside chamfer only
translate([36, 0, 0])
  u_profile(w = 8, h = 6, t = 1, l = 20, chamfer_outside = 0.8);

// 5. Outside fillet only
translate([48, 0, 0])
  u_profile(w = 8, h = 6, t = 1, l = 20, fillet_outside = 0.8);

// 6. Inner chamfer + outside chamfer
translate([0, 10, 0])
  u_profile(w = 8, h = 6, t = 1, l = 20, chamfer = 0.8, chamfer_outside = 0.5);

// 7. Inner fillet + outside fillet
translate([12, 10, 0])
  u_profile(w = 8, h = 6, t = 1, l = 20, fillet = 0.8, fillet_outside = 0.5);

// 8. Heavy wall thickness
translate([24, 10, 0])
  u_profile(w = 10, h = 8, t = 2.5, l = 20, fillet = 1, fillet_outside = 0.6);

// 9. Centered on all axes
color("#C1E1C1")
  translate([38, 10, 0])
    u_profile(w = 8, h = 6, t = 1, l = 20, fillet = 0.5, fillet_outside = 0.3, center = true);`,
  },
  {
    name: 'O-Profile',
    category: 'Profiles',
    code: `// Rectangular tube (box section) — all parameter variations
$fn = 32;

// 1. Plain O-profile (sharp corners)
o_profile(w = 8, h = 6, t = 1, l = 20);

// 2. Inner chamfer only
translate([12, 0, 0])
  o_profile(w = 8, h = 6, t = 1, l = 20, chamfer = 0.8);

// 3. Inner fillet only
translate([24, 0, 0])
  o_profile(w = 8, h = 6, t = 1, l = 20, fillet = 0.8);

// 4. Outside chamfer only
translate([36, 0, 0])
  o_profile(w = 8, h = 6, t = 1, l = 20, chamfer_outside = 0.8);

// 5. Outside fillet only
translate([48, 0, 0])
  o_profile(w = 8, h = 6, t = 1, l = 20, fillet_outside = 0.8);

// 6. Inner chamfer + outside chamfer
translate([0, 10, 0])
  o_profile(w = 8, h = 6, t = 1, l = 20, chamfer = 0.8, chamfer_outside = 0.5);

// 7. Inner fillet + outside fillet
translate([12, 10, 0])
  o_profile(w = 8, h = 6, t = 1, l = 20, fillet = 0.8, fillet_outside = 0.5);

// 8. Heavy wall thickness
translate([24, 10, 0])
  o_profile(w = 10, h = 8, t = 2.5, l = 20, fillet = 1, fillet_outside = 0.6);

// 9. Centered on all axes
color("#FFD1A9")
  translate([38, 10, 0])
    o_profile(w = 8, h = 6, t = 1, l = 20, fillet = 0.5, fillet_outside = 0.3, center = true);`,
  },
  {
    name: 'Attach: All Faces',
    category: 'Layout',
    code: `// attach(face, BOTTOM) — cylinder bottom meets parent face, pointing outward
$fn = 32;
cube([80, 80, 80], center = true) {
  attach(TOP, BOTTOM)    cylinder(h = 40, r = 10);
  attach(BOTTOM, BOTTOM) cylinder(h = 40, r = 10);
  attach(RIGHT, BOTTOM)  cylinder(h = 40, r = 10);
  attach(LEFT, BOTTOM)   cylinder(h = 40, r = 10);
  attach(FRONT, BOTTOM)  cylinder(h = 40, r = 10);
  attach(BACK, BOTTOM)   cylinder(h = 40, r = 10);
}`,
  },
  {
    name: 'Attach: Spin & Overlap',
    category: 'Layout',
    code: `// spin rotates child around attachment axis
// overlap > 0 sinks child into parent, < 0 adds gap
$fn = 32;
cube([100, 100, 100], center = true) {
  // Default: no spin, no overlap
  attach(TOP, BOTTOM) cube([40, 20, 30]);

  // spin = 45°: child rotated 45° around attachment axis
  attach(FRONT, BOTTOM, spin = 45) cube([40, 20, 30]);

  // overlap = 20: child sinks 20 units into parent
  attach(RIGHT, BOTTOM, overlap = 20) cylinder(h = 50, r = 15);

  // overlap = -20: 20-unit gap between parent face and child
  attach(LEFT, BOTTOM, overlap = -20) cylinder(h = 50, r = 15);
}`,
  },
  {
    name: 'Anchor on Primitives',
    category: 'Layout',
    code: `// anchor shifts the shape's origin to the anchor point
// Without anchor, cube() has its corner at origin
$fn = 32;

// Default cube: corner at origin
cube([40, 40, 40]);

// anchor = CENTER: center at origin (same as center=true)
translate([60, 0, 0])
  cube([40, 40, 40], anchor = CENTER);

// anchor = TOP: top face at origin, cube hangs below
translate([120, 0, 0])
  cube([40, 40, 40], anchor = TOP);

// anchor = BOTTOM: bottom at origin, cube sits above
translate([180, 0, 0])
  cube([40, 40, 40], anchor = BOTTOM);

// Practical: position(TOP) + anchor = BOTTOM = flush stacking
cube([100, 100, 30], center = true) {
  position(TOP)
    cube([40, 40, 60], anchor = BOTTOM);
}`,
  },
  {
    name: 'Attach: Edge & Corner',
    category: 'Layout',
    code: `// attach() works with combined anchors for edges/corners
$fn = 32;
cube([100, 100, 100], center = true) {
  // Edge: child points outward from edge midpoint
  attach(TOP + FRONT, BOTTOM) sphere(r = 10);
  attach(TOP + RIGHT, BOTTOM) sphere(r = 10);

  // Corner: child points outward from corner
  attach(TOP + FRONT + RIGHT, BOTTOM) sphere(r = 8);
  attach(BOTTOM + BACK + LEFT, BOTTOM) sphere(r = 8);
}`,
  },
  {
    name: 'Attach: Face Alignment',
    category: 'Layout',
    code: `// align positions the child within the face plane
// face determines orientation, align determines position
$fn = 32;
cube([40, 40, 40], center = true) {
  // Front face, left-bottom corner
  attach(FRONT, BOTTOM, align = LEFT + BOTTOM)
    cylinder(h = 10, r = 3);

  // Front face, right-top corner
  attach(FRONT, BOTTOM, align = RIGHT + TOP)
    cylinder(h = 10, r = 3);

  // Top face, back-right corner
  attach(TOP, BOTTOM, align = BACK + RIGHT)
    cylinder(h = 10, r = 3);

  // Right face, center-bottom
  attach(RIGHT, BOTTOM, align = BOTTOM)
    cylinder(h = 10, r = 3);
}`,
  },
  {
    name: 'Attach: Cut All Faces',
    category: 'Layout',
    code: `// mode="cut" subtracts the child from the parent
// attach(face, TOP) places the cylinder tip at the face, cutting inward
$fn = 32;
cube([20, 20, 20]) {
  attach(RIGHT, TOP, mode = "cut")  cylinder(h = 1, r = 5);
  attach(LEFT, TOP, mode = "cut")   cylinder(h = 1, r = 5);
  attach(TOP, TOP, mode = "cut")    cylinder(h = 1, r = 5);
  attach(BOTTOM, TOP, mode = "cut") cylinder(h = 1, r = 5);
  attach(FRONT, TOP, mode = "cut")  cylinder(h = 1, r = 5);
  attach(BACK, TOP, mode = "cut")   cylinder(h = 1, r = 5);
}`,
  },
  {
    name: 'Torus',
    category: 'Shapes',
    code: `// Torus — revolved circle profile
$fn = 48;

// Standard torus
torus(r1 = 5, r2 = 1.5);

// Partial torus (270 degrees)
color("#B5D4FF")
  translate([0, 0, 5])
    torus(r1 = 4, r2 = 1, angle = 270);`,
  },
  {
    name: 'Pipe',
    category: 'Shapes',
    code: `// Pipe — hollow cylinder (revolved rectangle)
$fn = 48;

// Pipe with wall thickness
pipe(h = 10, r = 5, t = 1);

// Pipe with inner radius
translate([14, 0, 0])
  pipe(h = 10, r = 5, r_inner = 3);

// Centered pipe
color("#C1E1C1")
  translate([28, 0, 0])
    pipe(h = 10, r = 5, t = 1.5, center = true);`,
  },
  {
    name: 'Ellipsoid',
    category: 'Shapes',
    code: `// Ellipsoid — scaled sphere
$fn = 48;

// Vector radius
ellipsoid(r = [3, 2, 1]);

// Per-axis overrides
translate([8, 0, 0])
  ellipsoid(rx = 1, ry = 3, rz = 2);

// Scalar radius (same as sphere)
color("#FFD1A9")
  translate([-8, 0, 0])
    ellipsoid(r = 2);`,
  },
  {
    name: 'Wedge',
    category: 'Shapes',
    code: `// Wedge — triangular prism
// Triangle in XZ: (0,0)→(w,0)→(0,h), extruded along Y (depth)

// Simple wedge
wedge([6, 4, 3]);

// Centered wedge
color("#D4B5FF")
  translate([10, 0, 0])
    wedge([6, 4, 3], center = true);

// Scalar size (cube-sized wedge)
translate([20, 0, 0])
  wedge(4);`,
  },
  {
    name: 'Regular Polygon',
    category: 'Shapes',
    code: `// Regular polygon — n-sided, rendered as thin 2D layer
$fn = 32;

// Pentagon
regular_polygon(r = 3, n = 5);

// Octagon
translate([8, 0, 0])
  regular_polygon(r = 3, n = 8);

// Triangle
translate([16, 0, 0])
  regular_polygon(r = 3, n = 3);

// Extruded hexagonal prism
translate([0, 8, 0])
  linear_extrude(height = 4)
    regular_polygon(r = 3, n = 6);`,
  },
  {
    name: 'Hexagon',
    category: 'Shapes',
    code: `// Hexagon — regular 6-sided polygon
$fn = 32;

// Flat hexagon (2D, thin layer)
hexagon(r = 3);

// Extruded hex bolt head
translate([8, 0, 0])
  linear_extrude(height = 3)
    hexagon(r = 4);

// Hex grid
translate([0, 10, 0])
  for (row = [0:2])
    for (col = [0:3])
      translate([col * 5 + (row % 2) * 2.5, row * 4.33, 0])
        linear_extrude(height = 1)
          hexagon(r = 2.3);`,
  },
  {
    name: 'Slot',
    category: 'Shapes',
    code: `// Slot (stadium/obround) — rectangle with semicircle caps
$fn = 32;

// Horizontal slot (w > h)
slot(w = 8, h = 3);

// Vertical slot (h > w)
translate([12, 0, 0])
  slot(w = 3, h = 8);

// Centered slot
translate([0, 6, 0])
  slot(w = 10, h = 4, center = true);

// Extruded slot
translate([0, 12, 0])
  linear_extrude(height = 2)
    slot(w = 8, h = 3, center = true);`,
  },
  {
    name: 'Star',
    category: 'Shapes',
    code: `// Star polygon — alternating outer/inner radius
$fn = 32;

// 5-pointed star
star(points = 5, r_outer = 4, r_inner = 1.5);

// 8-pointed star
translate([10, 0, 0])
  star(points = 8, r_outer = 4, r_inner = 2.5);

// Extruded star
translate([0, 10, 0])
  linear_extrude(height = 2, scale = 0.8)
    star(points = 6, r_outer = 4, r_inner = 2);`,
  },
  {
    name: 'Capsule',
    category: 'Shapes',
    code: `// Capsule — pill shape (cylinder + hemisphere caps)
$fn = 48;

// Standard capsule
capsule(h = 10, r = 2);

// Centered capsule
color("#FFB5BA")
  translate([8, 0, 0])
    capsule(h = 12, r = 2.5, center = true);

// Degenerate case: becomes sphere when h <= 2r
translate([16, 0, 0])
  capsule(h = 3, r = 2);`,
  },
  {
    name: 'Rounded Cube',
    category: 'Shapes',
    code: `// Rounded cube — hull of 8 spheres at inset corners
$fn = 32;

// Standard rounded cube
rounded_cube([8, 6, 4], r = 0.8);

// Centered
color("#B5E5FF")
  translate([12, 0, 0])
    rounded_cube([8, 6, 4], r = 1, center = true);

// Scalar size
translate([24, 0, 0])
  rounded_cube(5, r = 1);

// Large radius = very round
color("#C1E1C1")
  translate([0, 10, 0])
    rounded_cube([8, 8, 8], r = 3, center = true);`,
  },
  {
    name: 'T-Profile',
    category: 'Profiles',
    code: `// T-shaped beam — web + top flange
$fn = 32;

// 1. Plain T-profile (sharp corners)
t_profile(w = 8, h = 8, t = 1, l = 20);

// 2. Inner chamfer
translate([12, 0, 0])
  t_profile(w = 8, h = 8, t = 1, l = 20, chamfer = 0.8);

// 3. Inner fillet
translate([24, 0, 0])
  t_profile(w = 8, h = 8, t = 1, l = 20, fillet = 0.8);

// 4. Different web thickness
translate([36, 0, 0])
  t_profile(w = 8, h = 8, t = 1.5, tw = 0.8, l = 20, fillet = 0.5);

// 5. Centered
color("#B5D4FF")
  translate([48, 0, 0])
    t_profile(w = 8, h = 8, t = 1, l = 20, chamfer = 0.5, center = true);`,
  },
  {
    name: 'I-Profile',
    category: 'Profiles',
    code: `// I-beam — bottom flange + web + top flange
$fn = 32;

// 1. Plain I-profile (sharp corners)
i_profile(w = 8, h = 10, t = 1.5, l = 20);

// 2. Inner chamfer
translate([12, 0, 0])
  i_profile(w = 8, h = 10, t = 1.5, l = 20, chamfer = 0.8);

// 3. Inner fillet
translate([24, 0, 0])
  i_profile(w = 8, h = 10, t = 1.5, l = 20, fillet = 0.8);

// 4. Different web thickness
translate([36, 0, 0])
  i_profile(w = 8, h = 10, t = 1.5, tw = 1, l = 20, fillet = 0.5);

// 5. Centered
color("#C1E1C1")
  translate([48, 0, 0])
    i_profile(w = 8, h = 10, t = 1.5, l = 20, chamfer = 0.5, center = true);`,
  },
  {
    name: 'Thread',
    category: 'Hardware',
    code: `// Helical screw thread
$fn = 48;

// Standard M10 thread
thread(d = 10, pitch = 1.5, length = 15);

// Coarse thread, multi-start
color("#FFE4B5")
  translate([14, 0, 0])
    thread(d = 10, pitch = 3, length = 15, starts = 2);

// Centered thread
color("#B5D4FF")
  translate([28, 0, 0])
    thread(d = 8, pitch = 1.25, length = 12, center = true);`,
  },
  {
    name: 'Spring',
    category: 'Hardware',
    code: `// Helical coil spring
$fn = 32;

// Standard spring
spring(r = 5, r_wire = 0.5, pitch = 3, turns = 5);

// Tight spring
color("#FFB5BA")
  translate([14, 0, 0])
    spring(r = 4, r_wire = 0.8, pitch = 2, turns = 8);

// Centered
color("#C1E1C1")
  translate([28, 0, 0])
    spring(r = 5, r_wire = 0.4, pitch = 4, turns = 3, center = true);`,
  },
  {
    name: 'Bolt & Nut',
    category: 'Hardware',
    code: `// ISO metric bolt and nut — M10 examples
$fn = 48;

// Plain bolt (no thread)
bolt("M10", length = 30);

// Bolt with thread
translate([25, 0, 0])
  bolt("M10", length = 30, thread_length = 15);

// Plain nut
translate([50, 0, 0])
  nut("M10");

// Threaded nut
translate([75, 0, 0])
  nut("M10", thread = true);`,
  },
  {
    name: 'Enclosure (Basic)',
    category: 'Enclosures',
    code: `// Simple enclosure — no mounting hardware
$fn = 32;
enclosure(
  size = [60, 40, 25],
  wall = 2,
  r = 3
);`,
  },
  {
    name: 'Enclosure (Bolt Mount)',
    category: 'Enclosures',
    code: `// Enclosure with bolt mount holes
// Hex counterbores in lid, nut pockets on floor bottom
$fn = 32;
enclosure(
  size = [80, 50, 30],
  wall = 2,
  r = 4,
  mount = "bolt",
  screw_size = "M3",
  boss_r = 4,
  screw_inset = 8,
  nut_pocket = true,
  part = "assembly"
);`,
  },
  {
    name: 'Enclosure (Screw Mount)',
    category: 'Enclosures',
    code: `// Enclosure with screw pilot holes in bosses
// hole_depth limits hole depth for self-tapping screws
$fn = 32;
enclosure(
  size = [70, 45, 28],
  wall = 2.5,
  r = 3,
  mount = "screw",
  screw_size = "M3",
  boss_r = 3.5,
  hole_depth = 12,
  part = "assembly"
);`,
  },
  {
    name: 'Enclosure (Filleted)',
    category: 'Enclosures',
    code: `// Enclosure with rounded fillet edges
$fn = 32;
enclosure(
  size = [60, 40, 25],
  wall = 2,
  r = 3,
  fillet_top = 1.5,
  fillet_bottom = 1.5,
  mount = "bolt",
  screw_size = "M3"
);`,
  },
  {
    name: 'Enclosure (Chamfered)',
    category: 'Enclosures',
    code: `// Enclosure with 45-degree chamfered edges
$fn = 32;
enclosure(
  size = [60, 40, 25],
  wall = 2,
  r = 3,
  chamfer_top = 1,
  chamfer_bottom = 1,
  mount = "screw",
  screw_size = "M3"
);`,
  },
  {
    name: 'Enclosure (Nut Pocket)',
    category: 'Enclosures',
    code: `// Bolt mount with hex nut pockets on floor bottom
// Flip the body to see nut recesses underneath
$fn = 32;
enclosure(
  size = [70, 45, 28],
  wall = 2.5,
  r = 3,
  mount = "bolt",
  screw_size = "M3",
  boss_r = 4,
  nut_pocket = true,
  part = "body"
);`,
  },
  {
    name: 'Enclosure (Exploded)',
    category: 'Enclosures',
    code: `// Body and lid separated for 3D printing
$fn = 32;
w = 70; d = 45; h = 28;

// Body — filleted bottom, bolt mounts with nut pockets
color("#C1E1C1")
  enclosure(size = [w, d, h], wall = 2, r = 4,
    fillet_bottom = 1.5, mount = "bolt",
    screw_size = "M3", boss_r = 4,
    nut_pocket = true, part = "body");

// Lid — chamfered top, hex counterbores for bolt heads
color("#B5D4FF")
  translate([0, 0, h + 5])
    enclosure(size = [w, d, h], wall = 2, r = 4,
      chamfer_top = 1, mount = "bolt",
      screw_size = "M3", boss_r = 4, part = "lid");`,
  },
  {
    name: 'Metric Bolt Sizes',
    category: 'Hardware',
    code: `// Row of ISO metric bolts — M3 through M16
$fn = 48;

bolt("M3", length = 12);

translate([12, 0, 0])
  bolt("M5", length = 16);

translate([28, 0, 0])
  bolt("M8", length = 20, thread_length = 10);

translate([48, 0, 0])
  bolt("M10", length = 25, thread_length = 12);

translate([72, 0, 0])
  bolt("M16", length = 35, thread_length = 18);`,
  },
  {
    name: 'Hollow Shell',
    category: 'Hardware',
    code: `// Shell: hollow out any shape
$fn = 48;

// Open-top box
shell(2, open = TOP) {
  cube([20, 20, 20], center = true);
}

// Closed hollow sphere
translate([30, 0, 0])
  shell(1.5) {
    sphere(r = 10);
  }`,
  },
  {
    name: 'Enclosure (Mount Points)',
    category: 'Enclosures',
    code: `// PCB enclosure with custom mount points
// Corner screws + 4 PCB standoff positions
$fn = 32;
enclosure(
  size = [80, 60, 30],
  wall = 2,
  r = 3,
  mount = "screw",
  screw_size = "M3",
  mount_points = [[20, 15], [60, 15], [20, 45], [60, 45]],
  part = "assembly"
);`,
  },
  {
    name: 'Standoff',
    category: 'Hardware',
    code: `// Mounting standoffs — pilot, through, and solid
$fn = 32;

// Pilot hole (default)
standoff(h = 10, screw_size = "M3");

// Through hole
translate([15, 0, 0])
  standoff(h = 10, screw_size = "M3", hole = "through");

// Solid (no hole)
translate([30, 0, 0])
  standoff(h = 10, screw_size = "M3", hole = "none");

// Custom dimensions
translate([45, 0, 0])
  standoff(h = 12, r = 5, hole_d = 3.5, hole_depth = 8);`,
  },
  {
    name: 'Standoff (Attached)',
    category: 'Hardware',
    code: `// Standoffs attached to a base plate
$fn = 32;

cube([60, 40, 3]) {
  // Corners
  attach(TOP + LEFT + FRONT, BOTTOM)
    standoff(h = 8, screw_size = "M3");

  attach(TOP + RIGHT + FRONT, BOTTOM)
    standoff(h = 8, screw_size = "M3");

  attach(TOP + LEFT + BACK, BOTTOM)
    standoff(h = 8, screw_size = "M3");

  attach(TOP + RIGHT + BACK, BOTTOM)
    standoff(h = 8, screw_size = "M3");

  // Center standoff — solid pillar
  attach(TOP, BOTTOM)
    standoff(h = 12, r = 5, hole = "none");
}`,
  },
  {
    name: 'Enclosure + Standoffs',
    category: 'Enclosures',
    code: `// Enclosure body with standoffs attached inside
$fn = 32;

enclosure(
  size = [80, 60, 30], wall = 2, r = 3,
  mount = "screw", screw_size = "M3",
  part = "body"
) {
  attach(TOP, BOTTOM, align = LEFT + FRONT)
    standoff(h = 6, screw_size = "M2");

  attach(TOP, BOTTOM, align = RIGHT + FRONT)
    standoff(h = 6, screw_size = "M2");

  attach(TOP, BOTTOM, align = LEFT + BACK)
    standoff(h = 6, screw_size = "M2");

  attach(TOP, BOTTOM, align = RIGHT + BACK)
    standoff(h = 6, screw_size = "M2");
}`,
  },
  {
    name: 'Hex-vented panel',
    category: 'Patterns',
    code: `// Hex hole pattern baked into a cube primitive.
// Pattern args use a "hole_" prefix to avoid clashing with the primitive's own
// d / r / h / center args. Leave rows/cols unset → fill mode auto-fits the shape.
$fn = 48;

cube([80, 50, 4],
  cut_pattern = "hex",
  spacing = 8,
  hole_d = 5,
  margin = 4,
  anchor = TOP);`,
  },
  {
    name: 'Grid pattern on cube',
    category: 'Patterns',
    code: `// Explicit rows/cols grid with through-holes.
$fn = 32;

cube([60, 40, 3],
  cut_pattern = "grid",
  rows = 3, cols = 7,
  spacing = 8,
  hole_d = 4,
  anchor = TOP);`,
  },
  {
    name: 'Ring of holes on cylinder flange',
    category: 'Patterns',
    code: `// Ring pattern through a cylinder — classic bolt-circle flange.
// Note: hole_count / hole_radius / hole_d are prefixed so they don't
// collide with the cylinder's own radius and diameter.
$fn = 64;

cylinder(h = 6, r = 30,
  cut_pattern = "ring",
  hole_count = 8,
  hole_radius = 22,
  hole_d = 4,
  anchor = TOP);`,
  },
  {
    name: 'Hex fill on irregular shape',
    category: 'Patterns',
    code: `// For irregular shapes, wrap them in hole_pattern() to use the pattern builder.
// Inside hole_pattern() the simple names (d, h, count, radius, center) work —
// only the cut_pattern=... form on primitives needs the "hole_" prefix.
// Cylinders that fall outside the silhouette subtract nothing.
$fn = 48;

module dogbone() {
  linear_extrude(6) union() {
    translate([-40, 0]) circle(15);
    translate([ 40, 0]) circle(15);
    square([80, 10], center = true);
  }
}

hole_pattern(pattern = "hex", spacing = 7, d = 3, margin = 3, anchor = TOP) {
  dogbone();
}`,
  },
  {
    name: 'Side-face hex pattern',
    category: 'Patterns',
    code: `// Anchor a pattern to a different face — here the RIGHT face of a block.
$fn = 32;

cube([40, 60, 40],
  cut_pattern = "hex",
  spacing = 7,
  hole_d = 3,
  margin = 5,
  anchor = RIGHT);`,
  },
  {
    name: 'Honeycomb (hex holes)',
    category: 'Patterns',
    code: `// cut_shape selects the CUTTER geometry — "hex" gives classic honeycomb.
// Combines any layout ("hex", "grid") with any shape ("circle", "hex", "triangle", "square").
$fn = 48;

cube([90, 60, 4],
  cut_pattern = "hex",
  cut_shape   = "hex",
  spacing = 9,
  hole_d  = 7,
  margin  = 4,
  anchor  = TOP);`,
  },
  {
    name: 'Isogrid (triangular tessellation)',
    category: 'Patterns',
    code: `// Classic isogrid — equilateral triangles alternating point-up / point-down,
// sharing edges so they tile the face. Common in aerospace for strength-to-weight.
//
// spacing = triangle edge length. hole_d = circumscribed diameter of each cutout
// (≈ spacing × 1.15 gives full tessellation, smaller values leave rib walls).
$fn = 32;

cube([120, 80, 4],
  cut_pattern = "isogrid",
  spacing = 16,
  hole_d  = 14,
  margin  = 4,
  anchor  = TOP);`,
  },
  {
    name: 'Square grid cutouts',
    category: 'Patterns',
    code: `// Grid layout with square cutouts (like a speaker grille).
$fn = 16;

cube([80, 50, 4],
  cut_pattern = "grid",
  cut_shape   = "square",
  spacing = 9,
  hole_d  = 6,
  margin  = 4,
  anchor  = TOP);`,
  },
  {
    name: 'Chamfered holes (countersunk)',
    category: 'Patterns',
    code: `// Chamfer widens the opening at top and bottom — classic countersunk-hole look.
// hole_chamfer = depth of the chamfer; opening widens by the same amount.
$fn = 48;

cube([80, 50, 6],
  cut_pattern  = "hex",
  spacing      = 10,
  hole_d       = 5,
  hole_chamfer = 1.2,
  margin       = 5,
  anchor       = TOP);`,
  },
  {
    name: 'Filleted holes',
    category: 'Patterns',
    code: `// hole_fillet gives a rounded transition at top and bottom.
// Stepped approximation (6 slices per end) — smooth enough for most prints.
$fn = 48;

cube([80, 50, 6],
  cut_pattern = "grid",
  spacing     = 10,
  rows = 3, cols = 7,
  hole_d      = 5,
  hole_fillet = 1.5,
  anchor      = TOP);`,
  },
  {
    name: 'Countersink top only (flat bottom)',
    category: 'Patterns',
    code: `// Per-edge chamfer: chamfer only on TOP, flat on BOTTOM.
// Same split works for fillet — use hole_fillet_top / hole_fillet_bot.
$fn = 48;

cube([80, 50, 6],
  cut_pattern      = "grid",
  spacing = 12, rows = 3, cols = 6,
  hole_d           = 5,
  hole_chamfer_top = 1.5,    // countersink from the top
  hole_chamfer_bot = 0,      // flat on the bottom
  anchor           = TOP);`,
  },
  {
    name: 'Mixed: fillet top + chamfer bottom',
    category: 'Patterns',
    code: `// Fillet on one side, chamfer on the other — lead-in at the bottom,
// rounded relief at the top. Works for any cut_shape.
$fn = 48;

cube([80, 50, 6],
  cut_pattern      = "hex",
  spacing          = 10,
  hole_d           = 5,
  hole_fillet_top  = 1.2,
  hole_chamfer_bot = 0.8,
  margin           = 4,
  anchor           = TOP);`,
  },
  {
    name: 'Plus (cross) cutouts',
    category: 'Patterns',
    code: `// Plus/cross cutter shape — two overlapping bars forming "+".
// hole_d = overall arm length, hole_thickness = arm width, hole_corner_r = rounded corners.
$fn = 32;

cube([100, 60, 4],
  cut_pattern     = "grid",
  cut_shape       = "plus",
  spacing         = 15,
  hole_d          = 10,
  hole_thickness  = 3,
  hole_corner_r   = 0.5,
  margin          = 4,
  anchor          = TOP);`,
  },
  {
    name: 'X cutouts (diagonal bars)',
    category: 'Patterns',
    code: `// X cutter shape — two overlapping bars rotated ±45° forming "X".
// Perfect for decorative ventilation grilles. Tile with pattern="grid".
$fn = 32;

cube([100, 60, 4],
  cut_pattern     = "grid",
  cut_shape       = "x",
  spacing         = 14,
  hole_d          = 10,
  hole_thickness  = 2.5,
  hole_corner_r   = 0.4,
  margin          = 4,
  anchor          = TOP);`,
  },
  {
    name: 'Plus / X as 2D profile',
    category: 'Patterns',
    code: `// plus() and x() are also 2D profiles — usable inside linear_extrude,
// offset(), difference(), or wherever circle/hexagon work.
$fn = 48;

// Extruded plus as a standalone part
translate([-25, 0, 0])
  linear_extrude(4) plus(size = 15, thickness = 4, r = 0.8);

// X with rounded corners
translate([0, 0, 0])
  linear_extrude(4) x(size = 15, thickness = 3, r = 0.6);

// Combined with offset for a border
translate([25, 0, 0])
  linear_extrude(4) offset(r = 1) plus(size = 12, thickness = 3);`,
  },
  {
    name: 'Bowtie grid (X ribs)',
    category: 'Patterns',
    code: `// Bowtie shape: ><  — two triangles apex-to-apex.
// When tiled in a grid, the remaining material between bowties naturally forms
// X-shaped ribs. hole_thickness controls the bowtie's mid-width; hole_d is the
// outer length (apex-to-apex tip of each triangle).
$fn = 32;

cube([120, 60, 4],
  cut_pattern    = "grid",
  cut_shape      = "bowtie",
  spacing        = 15,
  hole_d         = 12,
  hole_thickness = 6,
  hole_corner_r  = 0.5,
  margin         = 4,
  anchor         = TOP);`,
  },
  {
    name: 'Diamond grid (with gap)',
    category: 'Patterns',
    code: `// Diamond shape: <>  — two triangles pointing OUTWARD with a gap between them.
// hole_gap controls the space between their inner bases; gap = 0 gives a solid
// rhombus. Tile in a grid for a decorative panel look.
$fn = 32;

cube([120, 60, 4],
  cut_pattern    = "grid",
  cut_shape      = "diamond",
  spacing        = 14,
  hole_d         = 12,
  hole_thickness = 6,
  hole_gap       = 2,
  hole_corner_r  = 0.4,
  margin         = 4,
  anchor         = TOP);`,
  },
  {
    name: 'Bowtie with countersunk chamfer',
    category: 'Patterns',
    code: `// Flat chamfer on plus / x / bowtie / diamond uses hull-based frustums
// (truly flat slanted faces, no staircase). hole_corner_r is ignored when
// chamfer is set — flat faces take priority.
$fn = 32;

cube([100, 60, 6],
  cut_pattern      = "grid",
  cut_shape        = "bowtie",
  spacing          = 16,
  hole_d           = 13,
  hole_thickness   = 6,
  hole_chamfer_top = 1.2,
  hole_chamfer_bot = 0,
  margin           = 4,
  anchor           = TOP);`,
  },
  {
    name: 'Auto-fit (rib = uniform material width)',
    category: 'Patterns',
    code: `// Auto-fit: set pattern, shape, cell count, and rib width. Everything else
// (spacing, hole_d, thickness, hole_gap, margin) is derived so material is
// UNIFORM everywhere — between cells AND between spokes of a radial cutter.
//
// For radial shapes, the evaluator computes gap = rib/sin(π/N) and
// thickness = (2R-gap)*tan(π/N), making spoke sides parallel so the rib
// distance stays constant along the full spoke length.
$fn = 32;

// Linear row of 4-spoke radials — uniform 3mm material between cells AND between spokes
translate([-60, 0, 0])
  cube([100, 30, 4],
    cut_pattern = "linear",
    cut_shape   = "radial",
    hole_spokes = 4,
    hole_count  = 4,     // 4 cuts along the line
    rib         = 3,     // 3mm material between cells + between spokes
    anchor      = TOP);

// Grid of 6x3 hex cutters — same auto-fit idea
translate([60, 0, 0])
  cube([90, 45, 4],
    cut_pattern = "grid",
    cut_shape   = "hex",
    rows        = 3,
    cols        = 6,
    rib         = 2,
    anchor      = TOP);`,
  },
  {
    name: 'Robot arm link with radial lightening',
    category: 'Projects',
    code: `// Parametric arm link: circular end plates (with ring of mount holes)
// joined by a rectangular beam carrying a linear radial-spoke cut pattern.

// ─── Parameters ─────────────────────────────────────────────
length        = 80;    // joint-to-motor center distance
arm_thickness = 6;     // Z-height of the whole arm

joint_r       = 12;    // pivot-end disc radius
joint_bore    = 4;     // pivot shaft diameter
joint_bolt_n  = 6;     // ring of mount holes on joint disc
joint_bolt_r  = 7;     // joint-disc bolt-circle radius
joint_bolt_d  = 2.2;   // joint-disc mount hole diameter

motor_r       = 16;    // motor-end disc radius
motor_shaft   = 3;     // motor output shaft clearance
motor_bolt_n  = 4;     // ring of motor mount holes
motor_bolt_r  = 10;    // motor-disc bolt-circle radius
motor_bolt_d  = 2.5;   // motor-disc mount hole diameter

beam_w        = 18;    // middle beam width (Y)
beam_count    = 8;     // number of radial spoke cut-cells along the beam
beam_spokes   = 6;     // spokes per radial cutter (higher = more star points)
beam_rib      = 3;     // material between cells AND between spokes

$fn = 48;

// ─── Joint disc: ring of bolt holes + center bore ───────────
module joint_disc() {
  difference() {
    hole_pattern(pattern = "ring",
      count   = joint_bolt_n,
      radius  = joint_bolt_r,
      hole_d  = joint_bolt_d,
      anchor  = TOP) {
      cylinder(h = arm_thickness, r = joint_r);
    }
    translate([0, 0, -1])
      cylinder(h = arm_thickness + 2, r = joint_bore / 2);
  }
}

// ─── Motor disc: ring of bolt holes + center shaft hole ─────
module motor_disc() {
  difference() {
    hole_pattern(pattern = "ring",
      count   = motor_bolt_n,
      radius  = motor_bolt_r,
      hole_d  = motor_bolt_d,
      anchor  = TOP) {
      cylinder(h = arm_thickness, r = motor_r);
    }
    translate([0, 0, -1])
      cylinder(h = arm_thickness + 2, r = motor_shaft / 2);
  }
}

// ─── Middle beam: cube with linear row of radial-spoke cuts ─
module beam() {
  // Beam spans from joint disc to motor disc. Overlap a bit into each
  // disc so the union is watertight.
  overlap = 2;
  beam_len = length + overlap * 2;
  translate([length / 2, 0, arm_thickness / 2])
    cube([beam_len, beam_w, arm_thickness], center = true,
      cut_pattern     = "linear",
      cut_shape       = "radial",
      angle           = 0,            // cuts spaced along +X (arm's long axis)
      hole_count      = beam_count,
      hole_spokes     = beam_spokes,
      rib             = beam_rib,     // auto-fits spacing + thickness uniformly
      anchor          = TOP);
}

// ─── Assemble ───────────────────────────────────────────────
union() {
  translate([0, 0, 0])      joint_disc();
  translate([length, 0, 0]) motor_disc();
  beam();
}`,
  },
  {
    name: 'Spur gear — hub, helix, lightening holes',
    category: 'Gears',
    code: `// Full-featured spur gear — tweak the parameters at the top.
// Equivalent to the German "stirnrad" module with all its options.

Module         = 1;
teeth          = 30;
width          = 5;
bore           = 3;
hub            = true;
hub_diameter   = 6;
hub_thickness  = 5;
optimized      = false;
pressure_angle = 20;
helix_angle    = 20;
straight       = false;

$fn = 96;

final_helix_angle = straight ? 0 : helix_angle;
final_hub_d       = hub ? hub_diameter  : 0;
final_hub_h       = hub ? hub_thickness : 0;

spur_gear(
  mod            = Module,
  teeth          = teeth,
  width          = width,
  bore           = bore,
  hub_d          = final_hub_d,
  hub_h          = final_hub_h,
  pressure_angle = pressure_angle,
  helix_angle    = final_helix_angle,
  optimized      = optimized
);`,
  },
  {
    name: 'Spur gear + hole_pattern (triangles pointing to center)',
    category: 'Gears',
    code: `// Apply hole_pattern to a spur gear. The gear is the child of hole_pattern,
// which evaluates it, reads the bbox, and cuts a pattern through it.
// orient="radial" rotates each cutter so its apex points to the gear center.
$fn = 64;

hole_pattern(pattern = "ring", shape = "triangle",
  count  = 6,
  radius = 10,        // ring radius (inside the gear)
  d      = 5,         // cutter size
  orient = "radial",  // triangles point toward gear center
  chamfer = 0.4,
  anchor  = TOP) {
  spur_gear(teeth = 24, mod = 1.2, width = 5, bore = 4);
}`,
  },
  {
    name: 'Spur gear + radial spokes cutout',
    category: 'Gears',
    code: `// Single radial-star cutout in the center of a gear — classic "lightening"
// pattern for aerospace / robotic arm gears.
$fn = 64;

difference() {
  spur_gear(teeth = 32, mod = 1.5, width = 6, bore = 4);
  hole_pattern(pattern = "grid",
    rows = 1, cols = 1,
    shape   = "radial",
    spokes  = 5,
    d       = 25,
    rib     = 3,
    anchor  = TOP);
}`,
  },
  {
    name: 'Spur gear + ring of radial cutters',
    category: 'Gears',
    code: `// Ring of multi-spoke radial cutters around a gear face — combines two
// hole_pattern features: ring placement + radial shape with orient="radial".
$fn = 48;

hole_pattern(pattern = "ring", shape = "radial",
  spokes = 3,
  count  = 8,
  radius = 12,
  d      = 6,
  rib    = 2,
  orient = "radial",
  anchor = TOP) {
  spur_gear(teeth = 48, mod = 1, width = 5, bore = 5);
}`,
  },
  {
    name: 'Bolt holes (countersunk / counterbore)',
    category: 'Hardware',
    code: `// bolt_hole() — universal bolt drill with ISO metric sizes and head types.
// Types: "through" (plain), "countersunk" (cone pocket), "counterbore" (cylindrical pocket).
// Takes children to subtract from, OR returns just the drill shape (for manual placement).
$fn = 48;

// --- Style 1: standalone, takes children ---
// M5 countersunk hole through a panel, auto-length from bbox
translate([-60, 0, 0])
  bolt_hole("countersunk", "M5") {
    cube([30, 30, 6], center = true);
  }

// --- Style 2: return drill shape for explicit placement in a difference() ---
translate([0, 0, 0])
  difference() {
    cube([50, 30, 6], center = true);
    // 4 corner bolt holes
    for (x = [-18, 18], y = [-10, 10])
      translate([x, y, 0]) bolt_hole("countersunk", "M4");
  }

// --- Style 3: counterbore (socket-head cap screw) ---
translate([50, 0, 0])
  difference() {
    cube([40, 30, 8], center = true);
    for (x = [-12, 12])
      translate([x, 0, 0]) bolt_hole("counterbore", "M5");
  }

// --- Style 4: explicit dimensions (no size string) ---
translate([-60, -40, 0])
  bolt_hole(type = "countersunk",
    d = 6, head_d = 12, head_h = 3, clearance = 0.3) {
    cube([25, 25, 6], center = true);
  }`,
  },
  {
    name: 'Nut traps (hex pocket + bolt clearance)',
    category: 'Hardware',
    code: `// nut_hole() — hex pocket sized to capture an ISO metric nut, plus a
// through-hole for the bolt shank. Pocket depth + flats from M-size table.
// Pair with bolt_hole() on the opposite face for a full nut+bolt joint.
$fn = 48;

// --- Style 1: standalone, M5 nut trap on the BOTTOM face ---
translate([-60, 0, 0])
  nut_hole("M5", anchor = BOTTOM) {
    cube([30, 30, 10], center = true);
  }

// --- Style 2: bolt + nut joint (countersunk top, nut trap on bottom) ---
translate([0, 0, 0])
  bolt_hole("countersunk", "M4", anchor = TOP)
    nut_hole("M4", anchor = BOTTOM) {
      cube([30, 30, 12], center = true);
    }

// --- Style 3: row of nut traps in a clamp bar ---
translate([55, 0, 0])
  difference() {
    cube([50, 20, 10], center = true);
    for (x = [-15, 0, 15])
      translate([x, 0, 0]) nut_hole("M3", anchor = BOTTOM);
  }

// --- Style 4: explicit override (loose pocket, custom depth) ---
translate([-60, -40, 0])
  nut_hole("M5", s = 9, nut_h = 5, clearance = 0.3, anchor = BOTTOM) {
    cube([30, 30, 12], center = true);
  }`,
  },
  {
    name: 'Ring pattern — auto-fit on a disc',
    category: 'Patterns',
    code: `// Ring + rib auto-fit: hole_d derived from arc length / count.
// Each hole on the ring is (2π·radius / count − rib) mm across, with a clean
// rib mm of material between adjacent holes.
$fn = 48;

// Round holes spaced evenly — classic bolt-circle flange
translate([-50, 0, 0])
  hole_pattern(pattern = "ring",
    count = 8, radius = 22, rib = 3, anchor = TOP) {
    cylinder(h = 4, r = 35);
  }

// Radial star cutters on a ring — material is uniform between arms AND around the ring
translate([50, 0, 0])
  hole_pattern(pattern = "ring",
    shape  = "radial",
    spokes = 3,
    count  = 8, radius = 22, rib = 2.5, anchor = TOP) {
    cylinder(h = 4, r = 35);
  }`,
  },
  {
    name: 'Linear row of holes',
    category: 'Patterns',
    code: `// "linear" pattern: 1D repeat along a configurable axis.
// angle = direction in degrees (0 = +X, 90 = +Y, 45 = diagonal).
// hole_count = 0 (default) auto-fits from parent bbox along the chosen axis.
$fn = 32;

// Axis-aligned row (simplest case)
translate([-70, 0, 0])
  cube([100, 20, 4],
    cut_pattern = "linear",
    spacing     = 15,
    hole_d      = 5,
    margin      = 4,
    anchor      = TOP);

// Diagonal row on a wider panel
translate([50, 0, 0])
  cube([80, 80, 4],
    cut_pattern      = "linear",
    spacing          = 12,
    angle            = 45,
    hole_d           = 5,
    hole_chamfer_top = 0.8,
    anchor           = TOP);`,
  },
  {
    name: 'Radial spokes (N triangles around center)',
    category: 'Patterns',
    code: `// Generic "radial" cutter — N triangles arranged around the center.
// hole_spokes controls count. Special cases: 2 = bowtie, 4 = star4 (classic
// elevator/robot-arm pattern), 6 = hex-radial. Works with all patterns + flat
// chamfer. Useful for lightweight structural columns.
$fn = 32;

// 4-spoke radial (elevator pillar style)
translate([-45, 0, 0])
  cube([40, 40, 4],
    cut_pattern      = "grid",
    cut_shape        = "radial",
    hole_spokes      = 4,
    spacing          = 20,
    hole_d           = 14,
    hole_thickness   = 5,
    hole_gap         = 2,
    hole_chamfer_top = 1,
    anchor           = TOP);

// 6-spoke radial
translate([45, 0, 0])
  cube([40, 40, 4],
    cut_pattern      = "grid",
    cut_shape        = "radial",
    hole_spokes      = 6,
    spacing          = 20,
    hole_d           = 14,
    hole_thickness   = 3,
    hole_gap         = 2,
    hole_chamfer_top = 1,
    anchor           = TOP);`,
  },
  {
    name: 'Bowtie / Diamond as 2D profiles',
    category: 'Patterns',
    code: `// bowtie() and diamond() are also 2D profile modules — usable inside
// linear_extrude, offset, etc.
$fn = 48;

translate([-25, 0, 0])
  linear_extrude(4) bowtie(size = 20, thickness = 8, r = 1);

translate([0, 0, 0])
  linear_extrude(4) diamond(size = 20, thickness = 10, gap = 3, r = 0.6);

// Solid rhombus: diamond with gap = 0
translate([25, 0, 0])
  linear_extrude(4) diamond(size = 16, thickness = 10);`,
  },
  {
    name: 'Sketch: bracket profile',
    category: 'Sketch',
    code: `// 2D sketcher example.
//
// A sketchN = sketch(...) assignment is a "view" for the 2D editor. Place
// the cursor inside sketch(...), press "Edit Sketch" in the toolbar, and
// you can drag handles, add lines / arcs / beziers, etc. — every gesture
// rewrites this text.
//
// Tools available: Select (V), Line (L), Rect (R), Circle (C), Arc (A),
// Bezier (B), Spline (S), Dim. Shift-click for multi-select. Drag empty
// space for rubber-band select. "3D BG" in the toolbar aligns the 3D
// viewport to the sketch plane so you can trace features on the model.
$fn = 48;

sketch1 = sketch(plane="XZ", origin=[0, 0, 0], entities=[
  line(from=[0, 0],  to=[30, 0]),
  line(from=[30, 0], to=[30, 25]),
  arc (center=[20, 25], r=10, start=0, end=180),
  line(from=[10, 25], to=[10, 15]),
  arc (center=[5, 15],  r=5,  start=0, end=180),
  line(from=[0, 15], to=[0, 0])
]);

linear_extrude(6) polygon(sketch1);
`,
  },
  {
    name: 'Sketch: gasket with holes',
    category: 'Sketch',
    code: `// Multi-path sketch: an outer profile with internal cut-outs.
// The walker passes every closed chain as a separate polygon to
// CrossSection.ofPolygons with EvenOdd fill, so holes become holes.
$fn = 64;

gasket = sketch(plane="XY", origin=[0, 0, 0], entities=[
  // Outer rounded rectangle built from 4 arcs + 4 lines
  line(from=[-30, -15], to=[30, -15]),
  arc (center=[30, -10], r=5, start=-90, end=0),
  line(from=[35, -10], to=[35, 10]),
  arc (center=[30,  10], r=5, start=0,   end=90),
  line(from=[30, 15],  to=[-30, 15]),
  arc (center=[-30, 10], r=5, start=90,  end=180),
  line(from=[-35, 10], to=[-35, -10]),
  arc (center=[-30, -10], r=5, start=180, end=270),
  // Two bolt holes as standalone circles
  circle_entity(center=[-20, 0], r=3),
  circle_entity(center=[ 20, 0], r=3)
]);

linear_extrude(3) polygon(gasket);
`,
  },
  {
    name: 'Sketch + linear_extrude + chamfer',
    category: 'Sketch',
    code: `// chamfer() / fillet() wrap any 3D child, including a linear_extrude
// of a sketch. They reliably edge-treat *convex* silhouettes — outlines
// with holes hit a stricter invariant in the slab fallback and may
// silently pass through unchanged. For ringed / gasket / textual
// sketches use tapered_extrude (it has a more forgiving non-convex
// path baked in) or apply the chamfer to the outer shape and
// difference() the holes afterwards.

$fn = 64;

// Convex sketch: rounded bracket profile (no internal holes).
bracket = sketch(plane="XY", origin=[0, 0, 0], entities=[
  line(from=[-25, -15], to=[25, -15]),
  arc (center=[25, -10], r=5, start=-90, end=0),
  line(from=[30, -10], to=[30, 10]),
  arc (center=[25,  10], r=5, start=0,   end=90),
  line(from=[25, 15],  to=[-25, 15]),
  arc (center=[-25, 10], r=5, start=90,  end=180),
  line(from=[-30, 10], to=[-30, -10]),
  arc (center=[-25, -10], r=5, start=180, end=270),
]);

// (1) Both rims chamfered.
chamfer(c = 1.5)
  linear_extrude(8) polygon(bracket);

// (2) Top fillet only — softer "consumer" feel.
translate([0, 50, 0])
  fillet(r = 1.8, top = true, bottom = false)
    linear_extrude(8) polygon(bracket);

// (3) Need holes? Apply edge treatment first, then subtract:
translate([0, 100, 0])
  difference() {
    chamfer(c = 1.2)
      linear_extrude(8) polygon(bracket);
    translate([-15, 0, -1]) cylinder(h = 10, r = 3);
    translate([ 15, 0, -1]) cylinder(h = 10, r = 3);
  }
`,
  },
];
